// SPDX-License-Identifier: Apache-2.0

// cosineSim WGSL kernel — TypeScript dispatch wrapper.
// Bench harness for the tether-brain v2 first kernel port.
// Lowest-risk-first per Prometheus's MIGRATION.md.

import shaderSrc from "./cosineSim.wgsl?raw";

export interface CosineSimSetup {
  device: GPUDevice;
  pipeline: GPUComputePipeline;
  bindGroupLayout: GPUBindGroupLayout;
  paramsBuf: GPUBuffer;
  corpusBuf: GPUBuffer;
  queryBuf: GPUBuffer;
  scalesBuf: GPUBuffer;
  simsBuf: GPUBuffer;
  readbackBuf: GPUBuffer;
  nDocs: number;
  dim: number;
  dimPacked: number;
}

export interface CosineSimResult {
  sims: Float32Array;
  /** Wall time from command-encoder start through buffer-readback unmap. */
  totalMs: number;
}

export interface DeviceCaps {
  maxStorageBufferBindingSize: number;
  maxBufferSize: number;
  maxComputeWorkgroupStorageSize: number;
  shaderI8: boolean;
  shaderF16: boolean;
}

export async function createDevice(): Promise<{ device: GPUDevice; caps: DeviceCaps }> {
  if (!("gpu" in navigator)) {
    throw new Error("WebGPU not available — try Chrome or Edge with WebGPU enabled.");
  }
  const adapter = await navigator.gpu.requestAdapter();
  if (!adapter) throw new Error("No WebGPU adapter found.");

  // Ask for the adapter's actual maxima — M-class reports ~2 GB, default Vulkan is 128 MB.
  // 1M docs × 384 int8 needs 384 MB; capping at 256 MB silently broke the bind.
  const device = await adapter.requestDevice({
    requiredLimits: {
      maxStorageBufferBindingSize: adapter.limits.maxStorageBufferBindingSize,
      maxBufferSize: adapter.limits.maxBufferSize,
    },
  });

  const caps: DeviceCaps = {
    maxStorageBufferBindingSize: device.limits.maxStorageBufferBindingSize,
    maxBufferSize: device.limits.maxBufferSize,
    maxComputeWorkgroupStorageSize: device.limits.maxComputeWorkgroupStorageSize,
    shaderI8: device.features.has("shader-i8" as GPUFeatureName),
    shaderF16: device.features.has("shader-f16" as GPUFeatureName),
  };
  return { device, caps };
}

export async function setupCosineSim(
  device: GPUDevice,
  nDocs: number,
  dim: number,
): Promise<CosineSimSetup> {
  if (dim % 4 !== 0) throw new Error("dim must be a multiple of 4 for int8 packing");
  const dimPacked = dim / 4;

  // Hard fail with a clear message if the adapter can't fit the corpus.
  // (We previously got silent zeros when binding exceeded the limit.)
  const corpusBytes = nDocs * dimPacked * 4;
  if (corpusBytes > device.limits.maxStorageBufferBindingSize) {
    throw new Error(
      `corpus (${(corpusBytes / 1024 / 1024).toFixed(0)} MB) exceeds ` +
      `device.maxStorageBufferBindingSize (${(device.limits.maxStorageBufferBindingSize / 1024 / 1024).toFixed(0)} MB). ` +
      `Pick a smaller nDocs, or chunk the corpus across multiple bind groups.`,
    );
  }

  const module = device.createShaderModule({ code: shaderSrc, label: "cosineSim" });
  const bindGroupLayout = device.createBindGroupLayout({
    label: "cosineSim-bgl",
    entries: [
      { binding: 0, visibility: GPUShaderStage.COMPUTE, buffer: { type: "uniform" } },
      { binding: 1, visibility: GPUShaderStage.COMPUTE, buffer: { type: "read-only-storage" } },
      { binding: 2, visibility: GPUShaderStage.COMPUTE, buffer: { type: "read-only-storage" } },
      { binding: 3, visibility: GPUShaderStage.COMPUTE, buffer: { type: "read-only-storage" } },
      { binding: 4, visibility: GPUShaderStage.COMPUTE, buffer: { type: "storage" } },
    ],
  });
  const pipelineLayout = device.createPipelineLayout({
    bindGroupLayouts: [bindGroupLayout],
  });
  const pipeline = device.createComputePipeline({
    label: "cosineSim",
    layout: pipelineLayout,
    compute: { module, entryPoint: "main" },
  });

  const paramsBuf = device.createBuffer({
    label: "cosineSim-params",
    size: 16, // padded to 16 for std140-style alignment, even though only 8 used
    usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
  });
  const corpusBuf = device.createBuffer({
    label: "cosineSim-corpus",
    size: nDocs * dimPacked * 4,
    usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
  });
  const queryBuf = device.createBuffer({
    label: "cosineSim-query",
    size: dimPacked * 4,
    usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
  });
  const scalesBuf = device.createBuffer({
    label: "cosineSim-scales",
    size: (nDocs + 1) * 4,
    usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
  });
  const simsBuf = device.createBuffer({
    label: "cosineSim-sims",
    size: nDocs * 4,
    usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC,
  });
  const readbackBuf = device.createBuffer({
    label: "cosineSim-readback",
    size: nDocs * 4,
    usage: GPUBufferUsage.MAP_READ | GPUBufferUsage.COPY_DST,
  });

  return {
    device, pipeline, bindGroupLayout,
    paramsBuf, corpusBuf, queryBuf, scalesBuf, simsBuf, readbackBuf,
    nDocs, dim, dimPacked,
  };
}

export function uploadCorpus(setup: CosineSimSetup, corpus: Int8Array, scales: Float32Array): void {
  // Int8Array bytes upload as-is — host little-endian matches our WGSL extractBits LSB-first packing.
  setup.device.queue.writeBuffer(
    setup.corpusBuf, 0,
    corpus.buffer, corpus.byteOffset, corpus.byteLength,
  );
  setup.device.queue.writeBuffer(
    setup.scalesBuf, 0,
    scales.buffer, scales.byteOffset, scales.byteLength,
  );
}

export async function runCosineSim(
  setup: CosineSimSetup,
  query: Int8Array,
  queryScale: number,
): Promise<CosineSimResult> {
  const { device, pipeline, bindGroupLayout } = setup;

  // upload query bytes + write the trailing query-scale slot in scales buffer
  device.queue.writeBuffer(setup.queryBuf, 0, query.buffer, query.byteOffset, query.byteLength);
  device.queue.writeBuffer(
    setup.scalesBuf, setup.nDocs * 4,
    new Float32Array([queryScale]).buffer,
  );
  const paramsArr = new Uint32Array([setup.nDocs, setup.dimPacked, 0, 0]);
  device.queue.writeBuffer(setup.paramsBuf, 0, paramsArr.buffer);

  const bindGroup = device.createBindGroup({
    layout: bindGroupLayout,
    entries: [
      { binding: 0, resource: { buffer: setup.paramsBuf } },
      { binding: 1, resource: { buffer: setup.corpusBuf } },
      { binding: 2, resource: { buffer: setup.queryBuf } },
      { binding: 3, resource: { buffer: setup.scalesBuf } },
      { binding: 4, resource: { buffer: setup.simsBuf } },
    ],
  });

  const t0 = performance.now();
  const encoder = device.createCommandEncoder({ label: "cosineSim-encoder" });
  const pass = encoder.beginComputePass({ label: "cosineSim-pass" });
  pass.setPipeline(pipeline);
  pass.setBindGroup(0, bindGroup);
  pass.dispatchWorkgroups(Math.ceil(setup.nDocs / 64));
  pass.end();
  encoder.copyBufferToBuffer(setup.simsBuf, 0, setup.readbackBuf, 0, setup.nDocs * 4);
  device.queue.submit([encoder.finish()]);

  await setup.readbackBuf.mapAsync(GPUMapMode.READ);
  const sims = new Float32Array(setup.readbackBuf.getMappedRange().slice(0));
  setup.readbackBuf.unmap();
  const t1 = performance.now();

  return { sims, totalMs: t1 - t0 };
}

export function disposeCosineSim(setup: CosineSimSetup): void {
  setup.paramsBuf.destroy();
  setup.corpusBuf.destroy();
  setup.queryBuf.destroy();
  setup.scalesBuf.destroy();
  setup.simsBuf.destroy();
  setup.readbackBuf.destroy();
}

/**
 * Diagnostic: read back a sample of sims from the GPU buffer.
 * Use this to verify cosineSim actually wrote nonzero results.
 */
export interface SimsDiag {
  count: number;
  min: number;
  max: number;
  mean: number;
  countZero: number;
  countNaN: number;
  firstN: number[];
}

export async function peekSims(setup: CosineSimSetup, sampleCount = 32): Promise<SimsDiag> {
  const n = Math.min(sampleCount, setup.nDocs);
  const encoder = setup.device.createCommandEncoder({ label: "peekSims" });
  encoder.copyBufferToBuffer(setup.simsBuf, 0, setup.readbackBuf, 0, n * 4);
  setup.device.queue.submit([encoder.finish()]);
  await setup.readbackBuf.mapAsync(GPUMapMode.READ, 0, n * 4);
  const sims = new Float32Array(setup.readbackBuf.getMappedRange(0, n * 4).slice(0));
  setup.readbackBuf.unmap();

  // Stats over the full sims buffer would be ideal but we sized readback for nDocs anyway;
  // for now sample the first n.
  let min = Infinity;
  let max = -Infinity;
  let sum = 0;
  let countZero = 0;
  let countNaN = 0;
  for (let i = 0; i < sims.length; i++) {
    const v = sims[i] ?? 0;
    if (Number.isNaN(v)) {
      countNaN++;
      continue;
    }
    if (v === 0) countZero++;
    if (v < min) min = v;
    if (v > max) max = v;
    sum += v;
  }
  return {
    count: sims.length,
    min: min === Infinity ? 0 : min,
    max: max === -Infinity ? 0 : max,
    mean: sims.length > 0 ? sum / sims.length : 0,
    countZero,
    countNaN,
    firstN: Array.from(sims.slice(0, 8)),
  };
}
